import { useState, type ComponentType } from 'react';
import missions, { Mission } from '../../data/missions';
import type { CatalogTool } from '../../api/catalogApi';
import ToolCard from '../hub/ToolCard';
// import AmazonBendosReconPanel from '../AmazonBendosReconPanel/AmazonBendosReconPanel';
import PayoutReconPanel from '../PayoutReconPanel/PayoutReconPanel';
import MarketplaceReconPanel, { MarketplaceConfig } from './MarketplaceReconPanel';
import PaygwReconPanel from './PaygwReconPanel';
import PropertyReconPanel from './PropertyReconPanel';

interface PanelProps {
  mission: Mission;
  onBack: () => void;
}

interface ReconTool {
  /** Mission id (see `data/missions.ts`), which supplies the title, description and file types. */
  missionId: string;
  Panel: ComponentType<PanelProps>;
}

const ECOMMERCE: MarketplaceConfig = {
  endpoint: 'ecommerce-recon',
  platforms: [
    { value: 'amazon', label: 'Amazon' },
    { value: 'flipkart', label: 'Flipkart' },
    { value: 'meesho', label: 'Meesho' },
    { value: 'myntra', label: 'Myntra' },
  ],
  erpLabel: 'ERP (SAP) sales report',
  platformFileLabel: 'settlement report',
  defaultCycleDays: 15,
  optionalFiles: [],
  steps: [
    "Choose the marketplace and upload your ERP (SAP) sales report and that marketplace's settlement report.",
    'Orders are matched by Order ID. Payouts (or gross values) are compared within the tolerance you set.',
    'Cancelled / RTO / returned orders and exchange pairs are separated out instead of being flagged twice.',
    'Click Run Reconciliation, then download the Excel report.',
  ],
};

const QUICKCOMM: MarketplaceConfig = {
  endpoint: 'quickcomm-recon',
  platforms: [
    { value: 'blinkit', label: 'Blinkit' },
    { value: 'zepto', label: 'Zepto' },
  ],
  erpLabel: 'ERP sales report',
  platformFileLabel: 'settlement report',
  defaultCycleDays: 7,
  stockHoldingDays: true,
  optionalFiles: [
    { field: 'vendor_file', label: 'Vendor dispatch report', note: 'Units shipped to the dark store, one row per batch.' },
    { field: 'stock_file', label: 'Platform stock report', note: "The platform's own current-inventory export." },
    { field: 'erp_stock_file', label: 'ERP stock report', note: 'Your own stock position, to reconcile against the platform.' },
  ],
  steps: [
    "Choose Blinkit or Zepto and upload your ERP sales report and that platform's settlement report.",
    'Optionally add the vendor dispatch, platform stock and ERP stock reports for stock reconciliation and batch aging.',
    'Orders are matched by Order ID; stock by SKU. Batches held past the stock-holding limit are flagged.',
    'Click Run Reconciliation, then download the Excel report.',
  ],
};

const SwiggyZomatoPanel = (props: PanelProps) => (
  <PayoutReconPanel {...props} endpoint="/swiggy-recon/reconcile" backLabel="Reconciliation Tools" />
);
const QuickCommPanel = (props: PanelProps) => <MarketplaceReconPanel {...props} config={QUICKCOMM} />;
const EcommercePanel = (props: PanelProps) => <MarketplaceReconPanel {...props} config={ECOMMERCE} />;

// Add each new reconciliation here (plus its mission in `data/missions.ts` and its route prefix in
// `TOOL_ENDPOINTS['reconciliation-tools']` in backend/app/catalog.py).
const RECON_TOOLS: ReconTool[] = [
  // Hidden for now; uncomment to bring the Amazon Bendos card back.
  // { missionId: 'amazon-bendos-recon', Panel: AmazonBendosReconPanel },
  { missionId: 'swiggy-zomato-recon', Panel: SwiggyZomatoPanel },
  { missionId: 'property-recon', Panel: PropertyReconPanel },
  { missionId: 'quickcomm-recon', Panel: QuickCommPanel },
  { missionId: 'ecommerce-recon', Panel: EcommercePanel },
  { missionId: 'paygw-recon', Panel: PaygwReconPanel },
];

/** The hub's own card, so each reconciliation looks like any other tool card. */
function toCard(mission: Mission): CatalogTool {
  return {
    id: mission.id,
    name: mission.title,
    tag: 'Reconciliation',
    emoji: '',
    description: mission.description,
    mission_id: mission.id,
  };
}

function ReconToolsPanel() {
  const [openId, setOpenId] = useState<string | null>(null);

  const open = RECON_TOOLS.find((t) => t.missionId === openId);
  const openMission = open && missions.find((m) => m.id === open.missionId);
  if (open && openMission) {
    return <open.Panel mission={openMission} onBack={() => setOpenId(null)} />;
  }

  return (
    <div className="tool-grid">
      {RECON_TOOLS.map((tool, i) => {
        const m = missions.find((x) => x.id === tool.missionId);
        return m && <ToolCard key={m.id} sectionId="demo" index={i} tool={toCard(m)} onOpen={() => setOpenId(m.id)} />;
      })}
    </div>
  );
}

export default ReconToolsPanel;
